import React from 'react';
import { X, CheckCircle, Zap } from 'lucide-react';
import { useSubscription } from '../contexts/SubscriptionContext';
import { getPlanDisplayName, getNextPlan } from '../lib/planLimits';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  feature: string;
  currentLimit: number;
  nextPlanLimit: number | string;
  currentPlan: string;
}

const UpgradeModal: React.FC<UpgradeModalProps> = ({
  isOpen,
  onClose,
  feature,
  currentLimit,
  nextPlanLimit,
  currentPlan,
}) => {
  const { createCheckoutSession } = useSubscription();
  const [loading, setLoading] = React.useState(false);

  const nextPlan = getNextPlan(currentPlan);
  const nextPlanDisplayName = nextPlan ? getPlanDisplayName(nextPlan) : 'Professional';

  // Plan prices mapping
  const planPrices = {
    starter: 'price_1RwlKZRwStgTZIz3TiwgdA5q',
    professional: 'price_1RwlHVRwStgTZIz3AN01Zyj3',
    enterprise: 'price_1RwlItRwStgTZIz3xjB1fjR4',
  };

  const planDisplayPrices = {
    starter: '$9/month',
    professional: '$29/month',
    enterprise: '$199/month',
  };

  const handleUpgrade = async () => {
    if (!nextPlan) return;
    
    try {
      setLoading(true);
      const priceId = planPrices[nextPlan as keyof typeof planPrices];
      const url = await createCheckoutSession(priceId);
      
      if (!url) {
        throw new Error('Failed to create checkout session');
      }
      
      window.location.href = url;
    } catch (error) {
      // Silent error handling
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
          <div className="absolute top-0 right-0 pt-4 pr-4">
            <button
              type="button"
              className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              onClick={onClose}
            >
              <span className="sr-only">Close</span>
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="sm:flex sm:items-start">
            <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
              <Zap className="h-6 w-6 text-blue-600" />
            </div>
            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Upgrade Required
              </h3>
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  You've reached your {feature} limit of {currentLimit}. 
                  Upgrade to {nextPlanDisplayName} to get {nextPlanLimit === -1 ? 'unlimited' : nextPlanLimit} {feature.toLowerCase()}.
                </p>
              </div>
            </div>
          </div>

          {nextPlan && (
            <div className="mt-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">{nextPlanDisplayName} Plan</h4>
                    <p className="text-sm text-gray-500">
                      {planDisplayPrices[nextPlan as keyof typeof planDisplayPrices]}
                    </p>
                  </div>
                  <div className="flex items-center text-green-600">
                    <CheckCircle className="h-5 w-5 mr-1" />
                    <span className="text-sm font-medium">
                      {nextPlanLimit === -1 ? 'Unlimited' : nextPlanLimit} {feature.toLowerCase()}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="mt-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleUpgrade}
              disabled={loading || !nextPlan}
            >
              {loading ? 'Processing...' : `Upgrade to ${nextPlanDisplayName}`}
            </button>
            <button
              type="button"
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
              onClick={onClose}
            >
              Maybe Later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpgradeModal; 